
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, State, Mutation } from 'vuex-class';
import TopNavBar from '@/components/app/TopNavBar.vue';
import Loader from '@/components/Loader.vue';
// import Drawer from '@/components/app/Drawer.vue';
import { actions, mutations } from './store/types';
import { LoadDevicesActionType, LoadGroupsActionType } from './types/store/actions';
import { UserInfo } from './types/user-info';
import { DeviceInfo, DeviceType } from '@/types/device-info';
import { SetDeviceMutationType, SetDevicesMutationType } from '@/types/store/mutations';
import { EventTypes } from './constants/socketEventTypes';
import SocketIO from 'socket.io-client';
import { apiBaseUrl } from '@/config';
import store from '@/store';
import { DEVICE_HISTORY_UPDATE } from '@/constants/eventTypes';

@Component({
    components: {
        Loader,
        // Drawer,
        TopNavBar,
    },
})
export default class App extends Vue {
    @Getter('isLoggedOn')
    readonly isLoggedOn!: boolean;

    @State('devices')
    devices!: DeviceInfo[];

    @State('user')
    user!: UserInfo;

    @State('device')
    current!: DeviceInfo;

    @Action(actions.LOAD_DEVICES)
    loadDevices!: LoadDevicesActionType;

    @Action(actions.LOAD_GROUPS)
    loadGroups!: LoadGroupsActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    @Mutation(mutations.SET_DEVICES)
    setDevices!: SetDevicesMutationType;

    protected socket: any = null;
    protected reconnectSchedule: any = null;
    DeviceTypeenum = DeviceType;

    get hideControls() {
        return !this.isLoggedOn || this.$route.name === 'login';
    }

    handleDeviceMessage(data: any) {
        console.log(`Socket: Message received ->  {imei: ${data?.data?.imei}, eventType: ${data?.eventType}}`);
        if (data.eventType === EventTypes.DeviceInfoUpdate) {
            if (this.$route.name === 'device-overview' || this.$route.name === 'device-settings-alert') this.UpdateDeviceDetails(data.data);
        } else if (data.eventType === EventTypes.DeviceLocationHistoryInsert) {
            if (this.current.imei && data.data.imei === this.current.imei)
                window.dispatchEvent(new CustomEvent(DEVICE_HISTORY_UPDATE, { detail: data.data }));
        }
    }

    UpdateDeviceDetails(data: DeviceInfo) {
        const updatedDevices = this.devices.map((item) => {
            if (item.imei && item.imei === data.imei)
                return {
                    ...item,
                    lastSeenTime: data.lastSeenTime,
                    modem_state: data.modem_state,
                    modem_temperature: data.modem_temperature,
                    modem_temperature_f: data.modem_temperature_f,
                    modem_temperature_c: data.modem_temperature_c,
                    modem_voltage: data.modem_voltage,
                    psm_tau: data.psm_tau,
                    psm_active_time: data.psm_active_time,
                    edrx_value: data.edrx_value,
                    edrx_ptw: data.edrx_ptw,
                };
            return item;
        });
        this.setDevices({ devices: updatedDevices });

        if (this.current.imei && data.imei === this.current.imei) {
            this.setCurrentDevice({
                device: {
                    ...this.current,
                    lastSeenTime: data.lastSeenTime,
                    modem_state: data.modem_state,
                    modem_temperature: data.modem_temperature,
                    modem_temperature_f: data.modem_temperature_f,
                    modem_temperature_c: data.modem_temperature_c,
                    modem_voltage: data.modem_voltage,
                    psm_tau: data.psm_tau,
                    psm_active_time: data.psm_active_time,
                    edrx_value: data.edrx_value,
                    edrx_ptw: data.edrx_ptw,
                    alarm: data.alarm,
                    locationReportMode: data.locationReportMode,
                    operatingMode: data.operatingMode,
                },
            });
        }
    }

    getGPSDevicesIMEI = (devicesList: DeviceInfo[]) => {
        const list: string[] = [];
        devicesList.map((item: DeviceInfo) => {
            if (item.deviceType === this.DeviceTypeenum.AirboltGps && item.imei) list.push(item.imei);
        });
        return list;
    };

    // Socket
    initSocket() {
        const socket = SocketIO(apiBaseUrl, {
            extraHeaders: {
                authorization: store.getters.authToken,
            },
        });

        socket.on('connect', () => {
            console.log('Socket: connected');

            this.joinRoom('', 'user');
            if (this.reconnectSchedule) this.resetInterval();

            const currentDevices = this.getGPSDevicesIMEI(this.devices);
            currentDevices.map((item: string) => this.joinRoom(item));
        });

        socket.on('connect_error', (err) => {
            console.log('Socket: Connection error -> ', err.message);
            if (err.message === 'xhr poll error') {
                if (this.reconnectSchedule) this.resetInterval();
            } else if (this.isLoggedOn && this.reconnectSchedule == null) {
                this.reconnectSchedule = setInterval(() => {
                    this.socket = this.initSocket();
                }, 10000);
            }
        });

        socket.on('disconnect', () => console.log('Socket: disconnected'));

        // room
        socket.on(EventTypes.RoomConnect, (data) => console.log(`Socket: ${JSON.stringify(data)}`));
        socket.on(EventTypes.RoomConnectError, (data) => console.log(`Socket: ${JSON.stringify(data)}`));
        socket.on(EventTypes.RoomDisconnect, (data) => console.log(`Socket: ${JSON.stringify(data)}`));
        socket.on(EventTypes.RoomDisconnectError, (data) => console.log(`Socket: ${JSON.stringify(data)}`));

        // device
        socket.on(EventTypes.DeviceInfoUpdate, (data) => this.handleDeviceMessage(data));
        socket.on(EventTypes.DeviceLocationHistoryInsert, (data) => this.handleDeviceMessage(data));

        return socket;
    }
    resetInterval() {
        clearInterval(this.reconnectSchedule);
        this.reconnectSchedule = null;
    }
    joinRoom(id: string, type = 'device') {
        if (this.socket && this.socket.connected) this.socket.emit(EventTypes.JoinRoom, { id, type });
        else console.log(`Socket: join room error -> socket not connected`);
    }
    leaveRoom(id: string, type = 'device') {
        if (this.socket && this.socket.connected) this.socket.emit(EventTypes.LeaveRoom, { id, type });
    }
    // Socket

    @Watch('isLoggedOn', { immediate: true })
    async onIsLoggedOnChange() {
        if (this.isLoggedOn) {
            this.socket = this.initSocket();
        } else {
            if (this.reconnectSchedule) this.resetInterval();
            if (this.socket) {
                this.socket.close();
                this.socket = null;
            }
        }
    }

    @Watch('user')
    onUserChange(current: UserInfo | null, prev: UserInfo | null) {
        if (!prev && current) {
            this.loadDevices({ userId: this.user._id as string });
            this.loadGroups();
        }
    }

    @Watch('devices', { immediate: true })
    async onDevicesChange(current: DeviceInfo[] | [], prev: DeviceInfo[] | []) {
        if (this.socket && this.socket.connected) {
            let prevDevices: string[] = prev && prev.length > 0 ? this.getGPSDevicesIMEI(prev as DeviceInfo[]) : [];

            let currentDevices: string[] =
                current && current.length > 0 ? this.getGPSDevicesIMEI(current as DeviceInfo[]) : [];

            prevDevices.map((item: string) => {
                if (!currentDevices.includes(item)) this.leaveRoom(item);
            });

            currentDevices.map((item: string) => {
                if (!prevDevices.includes(item)) this.joinRoom(item);
            });
        }
    }
}
